/** Share card (1200x630 PNG) for a finished Manager Mode season. */
import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { CopySimple, DownloadSimple } from "@phosphor-icons/react";

const CARD_W = 1200;
const CARD_H = 630;
const PHASE_SHORT = ["GW1–6", "GW7–12", "GW13–18", "GW19–24", "GW25–30", "GW31–38"];

export default function ManagerShareCard({ report }) {
    const cardRef = useRef(null);
    const wrapRef = useRef(null);
    const [scale, setScale] = useState(0.5);
    const [busy, setBusy] = useState(false);
    const s = report.season_summary;

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const update = () => setScale(Math.min(1, el.clientWidth / CARD_W));
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    if (!s) return null;

    const render = async () => toPng(cardRef.current, {
        cacheBust: true, pixelRatio: 2, backgroundColor: "#0d0d0d",
        width: CARD_W, height: CARD_H,
        style: { transform: "scale(1)", transformOrigin: "top left" },
    });

    const download = async () => {
        setBusy(true);
        try {
            const url = await render();
            const a = document.createElement("a");
            a.href = url;
            a.download = `manazerska-sezona-${report.total_points}b.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success("PNG staženo");
        } catch {
            toast.error("Export obrázku selhal");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        setBusy(true);
        try {
            const url = await render();
            const blob = await (await fetch(url)).blob();
            await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
            toast.success("Zkopírováno do schránky — vlož na Discord (Ctrl+V)");
        } catch {
            toast.error("Kopírování selhalo — použij Stáhnout PNG");
        } finally {
            setBusy(false);
        }
    };

    const maxPhase = Math.max(...s.phase_points.map((p) => p.points), 1);

    return (
        <div className="space-y-2 text-left" data-testid="manager-share-card">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">Sdílej výsledek</span>
                <button type="button" onClick={download} disabled={busy} data-testid="share-card-download"
                        className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-fa-yellow text-black font-bold hover:bg-fa-yellowHover disabled:opacity-40">
                    <DownloadSimple size={11} weight="bold" className="inline mb-0.5 mr-1" />
                    {busy ? "Generuji…" : "Stáhnout PNG"}
                </button>
                <button type="button" onClick={copy} disabled={busy} data-testid="share-card-copy"
                        className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-white/25 text-white/75 hover:text-white hover:border-white/50 disabled:opacity-40">
                    <CopySimple size={11} weight="bold" className="inline mb-0.5 mr-1" /> Kopírovat pro Discord
                </button>
            </div>

            <div ref={wrapRef} className="w-full">
                <div className="overflow-hidden border border-white/10"
                     style={{ width: CARD_W * scale, height: CARD_H * scale }}>
                    <div ref={cardRef}
                         style={{ width: CARD_W, height: CARD_H, transform: `scale(${scale})`, transformOrigin: "top left" }}
                         className="bg-[#0d0d0d] relative flex flex-col p-12 border-2 border-fa-mint/30">
                    {/* header */}
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="font-mono text-[13px] uppercase tracking-[0.35em] text-fa-mint font-bold">Fantasy Academy</div>
                            <div className="font-display font-black text-[44px] uppercase leading-tight text-white">Manažerská sezóna</div>
                            <div className="font-mono text-[13px] uppercase tracking-widest text-white/45">Simulátor sezóny · GW1–38 · seed režim</div>
                        </div>
                        <div className="text-right">
                            <div className="font-display font-black text-[84px] leading-none text-fa-yellow tabular-nums">{report.total_points}</div>
                            <div className="font-mono text-[13px] uppercase tracking-widest text-white/55">bodů celkem</div>
                            <div className="font-mono text-[15px] text-fa-sky tabular-nums mt-1">
                                OR {report.overall_rank?.toLocaleString("cs")}
                            </div>
                        </div>
                    </div>

                    {/* phase bars */}
                    <div className="flex items-end gap-4 mt-8 h-[150px]">
                        {s.phase_points.map((p, i) => (
                            <div key={p.key} className="flex-1 flex flex-col items-center justify-end h-full">
                                <div className="font-mono text-[15px] text-white tabular-nums mb-1">{p.points}</div>
                                <div className="w-full bg-fa-mint/80" style={{ height: `${Math.max(8, (p.points / maxPhase) * 100)}px` }} />
                                <div className="font-mono text-[11px] uppercase tracking-widest text-white/40 mt-1.5">{PHASE_SHORT[i]}</div>
                            </div>
                        ))}
                    </div>

                    {/* highlights */}
                    <div className="grid grid-cols-3 gap-6 mt-8">
                        <div className="border border-white/12 bg-black/40 p-4">
                            <div className="font-mono text-[11px] uppercase tracking-widest text-fa-yellow mb-1.5">Nejlepší gameweek</div>
                            <div className="font-display font-black text-[28px] text-white leading-none tabular-nums">
                                GW{s.best_gw.gw} · {s.best_gw.points} b
                            </div>
                        </div>
                        <div className="border border-white/12 bg-black/40 p-4">
                            <div className="font-mono text-[11px] uppercase tracking-widest text-fa-yellow mb-1.5">Nejlepší přestup</div>
                            {s.best_transfer ? (
                                <div className="font-mono text-[16px] leading-snug">
                                    <span className="text-red-300">{s.best_transfer.out}</span>
                                    <span className="text-white/40"> → </span>
                                    <span className="text-fa-mint">{s.best_transfer.in}</span>
                                    <div className="text-white/55 text-[13px] mt-0.5 tabular-nums">
                                        {s.best_transfer.gain > 0 ? "+" : ""}{s.best_transfer.gain} b od GW{s.best_transfer.from_gw}
                                    </div>
                                </div>
                            ) : (
                                <div className="font-mono text-[16px] text-white/55">Sezóna bez přestupů 💪</div>
                            )}
                        </div>
                        <div className="border border-white/12 bg-black/40 p-4">
                            <div className="font-mono text-[11px] uppercase tracking-widest text-fa-yellow mb-1.5">Tahouni sezóny</div>
                            {s.top_squad.map((p, i) => (
                                <div key={i} className="flex justify-between font-mono text-[14px] py-px">
                                    <span className="text-white/85 truncate">{i + 1}. {p.name} <span className="text-white/35 text-[11px]">{p.position}</span></span>
                                    <span className="tabular-nums text-fa-mint ml-3">{p.points}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* footer */}
                    <div className="mt-auto flex items-center justify-between pt-6">
                        <span className="font-mono text-[12px] uppercase tracking-[0.3em] text-white/35">fantasy-academy · fpl timing analyzer</span>
                        <span className="font-mono text-[12px] uppercase tracking-widest text-fa-mint/70">Zvládneš víc? Odsimuluj vlastní sezónu →</span>
                    </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
